import { fetchdata } from 'apis/FetchData';
import { apiConfig } from 'config';
import { PRODUCT_TYPES } from 'common/utils/constants';
import { createDevice, getDeviceSwVersion, getMainUnitFromBundleMembers } from 'common/utils/helpers';

let filterRequestIndex = 0;
function getDefaultHeaders() {
  const auth = window.__APP_AUTH;
  return {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    Authorization: 'Bearer ' + auth?.accessToken,
  };
}

export function mapDeviceSearchResult(data) {
  let devices = [];
  let bundles = {};

  if (data?.devices) {
    devices = data.devices.map((device) => createDevice(device))
  }

  // data.bundles can be an empty object for some reason
  if (data?.bundles && data.bundles.length > 0) {
    bundles = data.bundles.reduce((acc, bundle) => {
      const result = { ...bundle };
      const mainUnit = getMainUnitFromBundleMembers(result.members);
      const mainUnitBundle = mainUnit?.tags?.bundle;

      if (result.name === '' || result.name == null) {
        result.name = mainUnitBundle?.name ? mainUnitBundle.name : 'Unnamed site';
      }

      if (result.type === '' || result.type == null) {
        result.type = mainUnitBundle?.type ? mainUnitBundle.type : 'Unknown';
      }

      if (result.code === '' || result.code == null) {
        result.code = mainUnitBundle?.code ? mainUnitBundle.code : 'POD_Unknown';
      }

      if (result.type === 'Pivotal 5G') {
        result.type = 'Pivot 5G';
      }

      result.members = result.members.map((member) => createDevice(member));

      acc[result.bundleId] = result;

      return acc;
    }, {});
  }

  return {
    devices,
    bundles,
  };
}

export function searchDevices(body = {}, format = true, apiVersion = null, abortController) {
  if (body.filters) {
    filterRequestIndex++;
    const filters = [{ or: [] }]; // or key will be used for doing an OR operation, example arr[0] || arr[1] || arr[2]
    for (const key in body.filters) {
      if (/tags.fm-alarms/.test(key)) filters[0].or.push({ [key]: body.filters[key] });
      else filters.push({ [key]: body.filters[key] }); // these will be AND'd, since they're in objects and objects are and'd by default;
    }
    if (filters[0].or.length > 1) body.filters = filters; // if greater than 1 alarm then we can use the parsed filter body with OR, otherwise no need
  }
  let currentFilterRequestIndex = filterRequestIndex;

  if (body['size'] == null) body['size'] = 10000;
  body['coalesceBundles'] = body.coalesceBundles ?? true;

  let url = apiVersion === 'v2' ? apiConfig.cmServiceDevicesSearchV2 : apiConfig.cmServiceDevicesSearch;

  return fetchdata(
    url,
    {
      method: 'POST',
      body: JSON.stringify(body),
      headers: getDefaultHeaders(),
      mode: 'cors',
      cache: 'no-cache',
    },
    true,
    abortController
  )
    .then((resJson) => ({ ...resJson, filterRequestIndex: currentFilterRequestIndex }))
    .catch((error) => console.error(error));
}

export function searchBundles(format = false) {
  let body = { coalesceBundles: true };

  return fetchdata(apiConfig.cmServiceDevicesSearch, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: getDefaultHeaders(),
    mode: 'cors',
    cache: 'no-cache',
  })
    .then((resJson) => {
      let bundlesDict = {};
      resJson.bundles &&
        resJson.bundles.forEach((bundle) => {
          bundlesDict[bundle.bundleId] = bundle;
          delete bundlesDict[bundle.bundleId].bundleId;
        });
      return bundlesDict;
    })
    .catch((error) => console.error(error));
}

export function searchDevicesIds() {
  let body = { returnFields: ['deviceId'] };
  body['size'] = 10000;

  return fetchdata(apiConfig.cmServiceDevicesSearch, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: getDefaultHeaders(),
    mode: 'cors',
    cache: 'no-cache',
  })
    .then((resJson) => {
      let devicesIds = [];
      resJson.devices &&
        resJson.devices.forEach((device) => {
          devicesIds.push(device.deviceId);
        });
      return devicesIds;
    })
    .catch((error) => console.error(error));
}

export const getFlattenedDeviceData = (device) => ({
  deviceId: device.deviceId,
  hasCriticalAlarms: device?.tags?.['fm-alarms']?.critical === true,
  connectionState: device.connectionState,
  adminState: device?.properties?.reported?.adminState,
  isBundled: device?.tags?.bundle?.bundleId !== undefined,
  productType: device?.properties?.reported?.productType,
  location: device?.properties?.reported?.location,
  softwareVersion: getDeviceSwVersion(device),
  swUpdateStatusUri: device?.tags?.iotHubDM?.softwareDownload?.statusQueryGetUri,
  ibmsApiVersion: device?.properties?.reported?.ibmsApiVersion,
  bundle: device?.tags?.bundle
});

const getDonorUnit = (members) => {
  let donor;
  members.forEach((member) => {
    const productType = member?.properties?.reported?.productType;
    if (productType && PRODUCT_TYPES.donor.includes(productType)) {
      donor = member;
    }
  });

  return donor;
};

export const searchDevicesByIdV2 = (query, type) => {
  let body = {
    size: 4,
    coalesceBundles: false,
    filters: {
      productType: PRODUCT_TYPES[type],
      text: query
    },
    returnFields: [
      'deviceId',
      'connectionState',
      'properties.reported.productType',
      'properties.reported.adminState',
      'properties.reported.location',
      'properties.reported.softwareLoad',
      'properties.reported.ibmsApiVersion',
      'tags.iotHubDM.softwareDownload',
      'tags.fm-alarms',
      'tags.bundle'
    ]
  };

  let selectedDeviceId = '';

  return fetchdata(apiConfig.cmServiceDevicesSearchV2, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: getDefaultHeaders(),
    mode: 'cors',
    cache: 'no-cache',
  })
    .then((resJson) => {
      let devices = [];
      resJson.devices &&
        resJson.devices.forEach((device) => {
          devices.push(getFlattenedDeviceData(device));
        });
      return devices;
    })
    .then((devices) => {
      if (devices.length === 0 || devices.length > 1) return devices;
      if (!devices[0].isBundled) return devices;

      const bundleSearchBody = {
        size: 1,
        coalesceBundles: true,
        filters: {
          text: devices[0].deviceId
        }
      };
      selectedDeviceId = devices[0].deviceId;

      return fetchdata(apiConfig.cmServiceDevicesSearchV2, {
        method: 'POST',
        body: JSON.stringify(bundleSearchBody),
        headers: getDefaultHeaders(),
        mode: 'cors',
        cache: 'no-cache',
      });
    })
    .then((bundleRes) => {
      if (!bundleRes.bundles) return bundleRes;

      let members = [];
      const donorUnit = getDonorUnit(bundleRes.bundles[0].members);
      members.push(getFlattenedDeviceData(donorUnit));
      let connectionState;
      let adminState;
      let hasCriticalAlarms;
      bundleRes.bundles[0].members.forEach((member) => {
        if (member.deviceId !== members[0].deviceId) {
          members.push(getFlattenedDeviceData(member));
        }
        if (member.deviceId === query) {
          connectionState = member.connectionState;
          adminState = member.properties?.reported?.adminState;
          hasCriticalAlarms = member?.tags?.['fm-alarms']?.critical === true;
        }
      });
      return [
        {
          deviceId: selectedDeviceId,
          bundleId: bundleRes.bundles[0].bundleId,
          isBundled: true,
          connectionState,
          adminState,
          hasCriticalAlarms,
          members
        }
      ];
    })
    .catch((error) => console.error(error));
};

export const checkSwStatus = (uri) => {
  return fetchdata(uri, {
    method: 'GET',
    headers: getDefaultHeaders(),
    mode: 'cors',
    cache: 'no-cache',
  })
    .then((resJson) => {
      return resJson;
    })
    .catch((error) => console.error(error));
};

export const searchDeviceVersion = (deviceId) => {
  let body = {
    size: 1,
    coalesceBundles: false,
    filters: {
      text: deviceId
    },
    returnFields: [
      'deviceId',
      'properties.reported.softwareLoad',
      'properties.reported.ibmsApiVersion',
      'connectionState',
      'properties.reported.adminState',
      'tags.fm-alarms'
    ]
  };

  return fetchdata(apiConfig.cmServiceDevicesSearchV2, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: getDefaultHeaders(),
    mode: 'cors',
    cache: 'no-cache'
  })
    .then((res) => ({
      deviceId,
      softwareVersion: getDeviceSwVersion(res.devices[0]),
      ibmsApiVersion: res.devices[0]?.properties?.reported?.ibmsApiVersion,
      connectionState: res.devices[0]?.connectionState,
      adminState: res.devices[0]?.properties?.reported?.adminState,
      hasCriticalAlarms: res.devices[0]?.tags?.['fm-alarms']?.critical === true
    }))
    .catch((error) => console.error(error));
};
